/* Production Summary — monthly, yearly and the data-management tab. */

import { useCallback, useRef, useState } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { production, unitLabel } from '../lib/api'
import type { MonthSummary, Unit } from '../lib/api'
import { useAsync } from '../lib/useAsync'
import { useCan } from '../lib/auth'
import { costPerKg } from '../lib/cost'
import {
  BarRow, Blueprint, Collapsible, Empty, ErrorBox, KpiRow, Labelled, Legend,
  LoadElsewhere, Loading, Note, NumberField, PanelHead, Segmented,
} from '../components/ui'
import { TargetActual } from '../components/charts/TargetActual'
import { Stacked, TrendLine } from '../components/charts/Stacked'
import {
  compactKg, dayOfMonth, longDate, monthName, num, pct, tonnes,
} from '../lib/format'
import { effFill } from '../lib/scale'
import { useIsDark } from '../lib/theme'
import { Heatmap } from '../components/charts/Heatmap'
import { TipRow, TipTitle } from '../components/charts/hover'
import { DailyTab } from './production/DailyTab'
import { SheetTab } from './production/SheetTab'

type Tab = 'daily' | 'month' | 'sheet' | 'year' | 'data'

export default function Production() {
  const [tab, setTab] = useState<Tab>('month')
  const [month, setMonth] = useState<string | undefined>(undefined)
  // One unit or the plant. Filters every tab but Data files; the server
  // allocates the plant-level meters and waste to the unit by output share.
  const [unit, setUnit] = useState<Unit | null>(null)
  // The day the Daily tab shows. Set by the heatmap, the day-by-day
  // register, or the Daily tab's own picker; null means "latest on file".
  const [day, setDay] = useState<string | null>(null)

  const openDay = (d: string) => { setDay(d); setTab('daily') }
  const goToData = () => setTab('data')

  return (
    <>
      <div className="no-print" style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 20, flexWrap: 'wrap', padding: '14px 32px',
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <Segmented<Tab>
          value={tab}
          minWidth={150}
          onChange={setTab}
          options={[
            { value: 'daily', label: 'Daily report' },
            { value: 'month', label: 'Monthly summary' },
            { value: 'sheet', label: 'Monthly summary sheet' },
            { value: 'year', label: 'Yearly summary' },
            { value: 'data', label: 'Data files' },
          ]}
        />
        {tab !== 'data' ? (
          <Labelled label="Unit">
            <select value={unit ?? 'all'} style={{ minWidth: 130 }}
              onChange={(e) => {
                setUnit(e.target.value === 'all' ? null : Number(e.target.value) as Unit)
                setDay(null)
              }}>
              <option value="all">Both units</option>
              <option value="1">Unit 1</option>
              <option value="2">Unit 2</option>
            </select>
          </Labelled>
        ) : null}
      </div>

      <main style={MAIN_STYLE}>
        {tab === 'daily' ? (
          <DailyGate month={month} unit={unit} day={day} setDay={setDay} goToData={goToData} />
        ) : null}
        {tab === 'month' ? (
          <MonthTab month={month} setMonth={setMonth} unit={unit}
            goToData={goToData} openDay={openDay} />
        ) : null}
        {tab === 'sheet' ? (
          <SheetTab month={month} setMonth={setMonth} unit={unit} goToData={goToData} />
        ) : null}
        {tab === 'year' ? (
          <YearTab unit={unit} openMonth={(m) => { setMonth(m); setTab('month') }} />
        ) : null}
        {tab === 'data' ? <DataTab /> : null}
      </main>
    </>
  )
}

/** The subline's account of what is in view: the plant, or one unit with
 *  the plant-level figures allocated to it. */
function scopeLine(data: MonthSummary, unit: Unit | null): string {
  return unit
    ? `${unitLabel(unit)} only · power and overhead allocated by output share`
    : `${data.installed.total} ring frames across two units`
}

// ==========================================================================
// Daily
// ==========================================================================
/** Loads the month the Daily tab draws its day list from, so DailyTab
 *  itself only has to worry about one day. */
function DailyGate({ month, unit, day, setDay, goToData }: {
  month: string | undefined
  unit: Unit | null
  day: string | null
  setDay: (d: string) => void
  goToData: () => void
}) {
  const { data, error, loading, reload } = useAsync(
    () => production.summary(month, unit), [month, unit])

  if (error) return <ErrorBox error={error} onRetry={reload} />
  if (loading && !data) return <Loading what="the month" />
  if (!data) return null
  if (data.empty) {
    return (
      <Empty title="No production data yet">
        Load the daily tracker workbook on the <b>Data files</b> tab and this fills in.
      </Empty>
    )
  }
  return (
    <>
      <Subline>
        One day of the tracker, ring frames · {monthName(data.month, true)}
        {unit ? ` · ${unitLabel(unit)} only · power and overhead allocated by output share` : ''}
      </Subline>
      <DailyTab month={data} unit={unit} date={day} setDate={setDay} goToData={goToData} />
    </>
  )
}

// ==========================================================================
// Monthly
// ==========================================================================
function MonthTab({ month, setMonth, unit, goToData, openDay }: {
  month: string | undefined
  setMonth: (m: string) => void
  unit: Unit | null
  goToData: () => void
  openDay: (date: string) => void
}) {
  const { data, error, loading, reload } = useAsync(
    () => production.summary(month, unit), [month, unit])
  const [dayIdx, setDayIdx] = useState<number | null>(null)
  // The benchmark is a management target, not a property of the data, so it
  // is set here rather than read from the server. null = use the default.
  const [benchOverride, setBenchOverride] = useState<number | null>(null)

  if (error) return <ErrorBox error={error} onRetry={reload} />
  if (loading && !data) return <Loading what="the month" />
  if (!data) return null
  if (data.empty) {
    return (
      <Empty title="No production data yet">
        Load the daily tracker workbook on the <b>Data files</b> tab and this fills in.
      </Empty>
    )
  }

  const sel = dayIdx === null || dayIdx >= data.days ? data.days - 1 : dayIdx
  const mName = monthName(data.month, true)
  const bench = benchOverride ?? data.benchmark

  return (
    <>
      <Subline>
        {mName} · {data.days} operating days · {scopeLine(data, unit)}
        {data.totals.unrated_rows
          ? ` · ${data.totals.unrated_rows} of ${num(data.totals.rows)} machine-shifts have no SITRA standard for the count typed`
          : ''}
        {data.totals.unrated_settings_rows
          ? ` · ${data.totals.unrated_settings_rows} have a settings standard too far from the output to use`
          : ''}
      </Subline>

      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <Labelled label="Month">
          <select value={data.month} onChange={(e) => { setMonth(e.target.value); setDayIdx(null) }}
            style={{ minWidth: 190 }}>
            {[...data.months].reverse().map((m) => (
              <option key={m} value={m}>{monthName(m, true)}</option>
            ))}
          </select>
        </Labelled>
        <button className="btn btn-secondary" style={{ alignSelf: 'flex-end' }} onClick={goToData}>
          Update data
        </button>
      </div>

      <Kpis data={data} mName={mName} bench={bench} />
      <DailyChart data={data} sel={sel} onSelect={setDayIdx} bench={bench}
        setBench={setBenchOverride} />

      {/* Unit split and power are per-day series across the whole month, so
          they get the full width; the three summary panels share a row. */}
      <UnitSplit data={data} unit={unit} />
      <Power data={data} />

      <section style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 24,
      }}>
        <Pareto data={data} />
        <CountMix data={data} />
        <Stock data={data} />
      </section>

      <EfficiencyHeatmap data={data} sel={sel} openDay={openDay} bench={bench} />
      <DayTable data={data} sel={sel} onSelect={setDayIdx} openDay={openDay} bench={bench} />

      <Footer data={data} />
    </>
  )
}

function Kpis({ data, mName, bench }: {
  data: MonthSummary; mName: string; bench: number
}) {
  const t = data.totals
  const short = mName.slice(0, 3)
  const dayLabel = (d: string | undefined) => (d ? `${dayOfMonth(d)} ${short}` : '–')
  // Power per kg is read over the metered days only; the overhead is spread
  // over every operating day, and a unit carries its output share of it.
  const cost = costPerKg(t.eb, t.solar, t.metered_kg, t.actual, data.days, t.output_share)

  return (
    <KpiRow items={[
      { label: 'Actual output', value: num(t.actual), unit: 'kg',
        note: `ring frames, ${data.days} operating days` },
      // Efficiency alone reads high while half the plant idles, so the
      // headline is the product and efficiency is quoted beside it.
      { label: 'Util × efficiency', value: t.util_x_eff.toFixed(1), unit: '%',
        tone: t.util_x_eff >= bench ? 'good' : 'bad',
        note: `${t.util_x_eff >= bench
          ? 'above'
          : `${(bench - t.util_x_eff).toFixed(1)} pts under`} the ${bench}% benchmark`
          + ` · ${t.utilization.toFixed(1)}% utilization × ${bothPct(t.efficiency_settings)}% settings efficiency` },
      { label: 'Efficiency · SITRA', value: t.efficiency.toFixed(1), unit: '%',
        note: `against SITRA gps × spindles · ${num(t.target)} kg target` },
      { label: 'Efficiency · settings', value: bothPct(t.efficiency_settings), unit: '%',
        note: `against the workbook's machine settings · ${num(t.target_settings)} kg target` },
      { label: 'Power per kg yarn', value: cost?.metered ? cost.ukg!.toFixed(2) : '–',
        unit: cost?.metered ? 'units' : '',
        note: cost?.metered
          ? `solar ${((t.solar / (t.eb + t.solar)) * 100).toFixed(1)}% of power`
            + (t.metered_days < data.days ? ` · ${t.metered_days} of ${data.days} days metered` : '')
          : 'no reliable meter readings this month' },
      { label: 'Production cost per kg', value: cost?.total != null ? `₹${cost.total.toFixed(0)}` : '–',
        unit: cost?.total != null ? '/kg' : '',
        note: !cost ? 'needs output'
          : cost.missing.length ? `waiting for ${cost.missing.join(', ')} — not set`
            : cost.metered
              ? `cotton ₹${cost.cotton!.toFixed(0)} · power ₹${cost.power!.toFixed(1)} · other ₹${cost.other!.toFixed(1)}`
              : 'power not metered this month' },
      { label: 'Shortfall', value: num(t.shortfall), unit: 'kg',
        note: 'unproduced against the SITRA target this month' },
      { label: 'Waste', value: t.waste_pct.toFixed(2), unit: '%',
        note: `${num(t.waste)} kg of soft and hard waste` },
      { label: 'Best day', value: dayLabel(data.best_day?.date),
        note: data.best_day
          ? `${num(data.best_day.actual)} kg · ${pct(data.best_day.util_x_eff)} util × eff` : '' },
      { label: 'Weakest day', value: dayLabel(data.worst_day?.date),
        note: data.worst_day
          ? `${num(data.worst_day.actual)} kg · ${pct(data.worst_day.util_x_eff)} util × eff` : '' },
    ]} />
  )
}

/** The benchmark the day bars and heatmap are graded against.
 *
 *  A target the mill sets, not a fact about the data, so it is adjustable
 *  here rather than fixed in config — and clamped to a range where the
 *  comparison still means something. */
function BenchmarkControl({ bench, setBench }: {
  bench: number; setBench: (v: number | null) => void
}) {
  const clamp = (v: number) => Math.max(50, Math.min(120, v))
  return (
    <div className="no-print" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 20, flexWrap: 'wrap', marginTop: 14, paddingTop: 12,
      borderTop: '1px solid var(--color-divider)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="axis-title">Utilization × efficiency benchmark</span>
        <div style={{
          display: 'flex', alignItems: 'stretch',
          border: '1px solid var(--color-divider)',
        }}>
          <button type="button" aria-label="Lower benchmark"
            onClick={() => setBench(clamp(bench - 1))}
            style={{
              font: 'inherit', fontSize: 14, lineHeight: 1, width: 26, border: 0,
              borderRight: '1px solid var(--color-divider)', background: 'transparent',
              color: 'var(--color-neutral-700)', cursor: 'pointer',
            }}>−</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 8px' }}>
            <NumberField className="num no-spin" min={50} max={120} step={1}
              value={bench}
              onChange={(v) => setBench(clamp(v))}
              style={{
                font: 'inherit', fontSize: 14, fontWeight: 600, width: 44,
                padding: '4px 0', textAlign: 'center', border: 0,
                background: 'transparent', color: 'var(--color-text)',
              }} />
            <span className="muted" style={{ fontSize: 12 }}>%</span>
          </div>
          <button type="button" aria-label="Raise benchmark"
            onClick={() => setBench(clamp(bench + 1))}
            style={{
              font: 'inherit', fontSize: 14, lineHeight: 1, width: 26, border: 0,
              borderLeft: '1px solid var(--color-divider)', background: 'transparent',
              color: 'var(--color-neutral-700)', cursor: 'pointer',
            }}>+</button>
        </div>
      </div>
      <Legend items={[
        { color: 'var(--c-bar1)', label: `At or above ${bench}%` },
        { color: 'var(--c-ink)', label: `Below ${bench}%` },
      ]} />
    </div>
  )
}

/** Shared x-axis furniture. Values live inside the plot now, so the axis
 *  carries only the date — day number, and weekday where there is room. */
function dayColumns(data: MonthSummary, withDow: boolean) {
  return data.dates.map((d, i) => (
    <div key={d} style={{ textAlign: 'center', lineHeight: 1.3 }}>
      <div className="num" style={{ fontSize: withDow ? 11 : 9, color: 'var(--color-neutral-700)' }}>
        {dayOfMonth(d)}
      </div>
      {withDow ? (
        <div style={{ fontSize: 9, color: 'var(--color-neutral-500)' }}>{data.series.dow[i]}</div>
      ) : null}
    </div>
  ))
}

function DailyChart({ data, sel, onSelect, bench, setBench }: {
  data: MonthSummary; sel: number; onSelect: (i: number) => void
  bench: number; setBench: (v: number | null) => void
}) {
  return (
    <Blueprint className="sheet">
      <PanelHead title="Daily output against target" right={
        <Legend items={[
          { color: 'var(--color-accent-500)', label: 'Target', outline: true },
          { color: 'var(--color-accent)', label: 'Actual' },
          { color: 'var(--color-accent-900)', label: 'Utilization × efficiency %', line: true },
        ]} />
      } />
      <Note>
        Kilograms per day across {data.unit ? unitLabel(data.unit === 'UNIT - 1' ? 1 : 2) : 'both units'},
        labelled in the chart with output
        and utilization × efficiency. The right axis carries that product; the
        dashed rule is the {bench}% benchmark. Select a day to open its detail.
      </Note>
      <TargetActual
        target={data.series.target}
        actual={data.series.actual}
        efficiency={data.series.util_x_eff}
        benchmark={bench}
        labelY={(v) => compactKg(v)}
        valueLabel={(i) => compactKg(data.series.actual[i])}
        tooltip={(i) => (
          <>
            <TipTitle>{dayOfMonth(data.dates[i])} {monthName(data.month, false).slice(0, 3)} · {data.series.dow[i]}</TipTitle>
            <TipRow label="Actual" value={`${num(data.series.actual[i])} kg`} color="var(--eff-strong)" />
            <TipRow label="Target" value={`${num(data.series.target[i])} kg`} color="var(--c-bar2)" />
            <TipRow label="Utilization" value={pct(data.series.utilization[i])} />
            <TipRow label="Efficiency · SITRA" value={pct(data.series.efficiency[i])} />
            <TipRow label="Efficiency · settings"
              value={data.series.efficiency_settings[i] != null
                ? pct(data.series.efficiency_settings[i] as number) : '–'} />
            <TipRow label="Util × eff" value={pct(data.series.util_x_eff[i])} />
            <TipRow label="Machines on"
              value={`${data.series.machines_on[i]} of ${data.installed.in_view}`} />
            <TipRow label="Waste" value={`${num(data.series.waste[i])} kg`} />
          </>
        )}
        xLabels={dayColumns(data, true)}
        onSelect={onSelect}
        selected={sel}
      />
      <BenchmarkControl bench={bench} setBench={setBench} />
      <div className="axis-title" style={{ textAlign: 'center', marginTop: 8 }}>
        Day of month
      </div>
    </Blueprint>
  )
}

type UnitPick = 'both' | 'u1' | 'u2'

function UnitSplit({ data, unit }: { data: MonthSummary; unit: Unit | null }) {
  const [local, setLocal] = useState<UnitPick>('both')
  // The page's unit picker overrides the panel's own.
  const pick: UnitPick = unit ? (unit === 1 ? 'u1' : 'u2') : local
  const { u1, u2 } = data.totals
  const share = (v: number) => Math.round((v / Math.max(1, u1 + u2)) * 100)

  // Filtering zeroes the hidden unit rather than dropping it, so the columns
  // stay on the same dates and the axis does not silently rescale to a
  // different set of days.
  const zeros = data.series.u1.map(() => 0)
  const lower = pick === 'u2' ? zeros : data.series.u1
  const upper = pick === 'u1' ? zeros : data.series.u2

  const shown = pick === 'u1' ? u1 : pick === 'u2' ? u2 : u1 + u2
  const legend = [
    ...(pick !== 'u2' ? [{ color: 'var(--c-bar3)', label: `Unit 1 · ${share(u1)}% of output` }] : []),
    ...(pick !== 'u1' ? [{ color: 'var(--c-bar4)', label: `Unit 2 · ${share(u2)}% of output` }] : []),
  ]

  return (
    <Blueprint className="sheet">
      <PanelHead title="Unit split" right={unit ? undefined : (
        <div className="no-print">
          <Segmented<UnitPick> value={pick} onChange={setLocal} options={[
            { value: 'both', label: 'Both units' },
            { value: 'u1', label: 'Unit 1' },
            { value: 'u2', label: 'Unit 2' },
          ]} />
        </div>
      )} />
      <Note>
        Daily kg by unit — Unit 1 (1,440 spindles a machine) below,
        Unit 2 (1,632 spindles) above. {num(shown)} kg over the month
        {pick === 'both' ? '' : ' from this unit'}.
      </Note>
      <Stacked
        height={240}
        lower={lower}
        upper={upper}
        colors={['var(--c-bar3)', 'var(--c-bar4)']}
        labelY={(v) => compactKg(v)}
        yTitle="Output kg"
        xLabels={dayColumns(data, true)}
        tooltip={(i) => (
          <>
            <TipTitle>{dayOfMonth(data.dates[i])} {monthName(data.month, false).slice(0, 3)} · {data.series.dow[i]}</TipTitle>
            {pick !== 'u2'
              ? <TipRow label="Unit 1" value={`${num(data.series.u1[i])} kg`} color="var(--c-bar3)" /> : null}
            {pick !== 'u1'
              ? <TipRow label="Unit 2" value={`${num(data.series.u2[i])} kg`} color="var(--c-bar4)" /> : null}
            {pick === 'both'
              ? <TipRow label="Both" value={`${num(data.series.u1[i] + data.series.u2[i])} kg`} /> : null}
          </>
        )}
      />
      <Legend items={legend} />
    </Blueprint>
  )
}

function Pareto({ data }: { data: MonthSummary }) {
  const rows = data.pareto.slice(0, 8)
  const max = rows.length ? rows[0].total : 1
  const top3 = data.pareto.slice(0, 3).reduce((a, r) => a + r.share, 0)

  return (
    <Blueprint className="sheet">
      <PanelHead title="Where the machines stopped" />
      <Note>
        Stoppage percentage summed over the month, {data.unit ? `${unitLabel(data.unit === 'UNIT - 1' ? 1 : 2)} only` : 'both units'}.
        The top three account for {Math.round(top3)}% of all lost time.
      </Note>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {rows.map((r) => (
          <BarRow key={r.category} label={r.category}
            width={(r.unit1 / max) * 300} secondWidth={(r.unit2 / max) * 300}
            value={pct(r.total)}
            tip={
              <>
                <TipTitle>{r.category}</TipTitle>
                <TipRow label="Unit 1" value={pct(r.unit1)} color="var(--c-bar3)" />
                <TipRow label="Unit 2" value={pct(r.unit2)} color="var(--c-bar4)" />
                <TipRow label="Combined" value={pct(r.total)} />
                <TipRow label="Share of lost time" value={`${Math.round(r.share)}%`} />
              </>
            } />
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 12 }}>
        Bars are percentage of available machine time lost — dark is Unit 1, light is Unit 2.
      </div>
    </Blueprint>
  )
}

function CountMix({ data }: { data: MonthSummary }) {
  const rows = data.count_mix.slice(0, 7)
  const max = rows.length ? rows[0].kg : 1
  return (
    <Blueprint className="sheet">
      <PanelHead title="Count mix" />
      <Note>Month output by yarn count, kg.</Note>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {rows.map((r) => (
          <BarRow key={r.count} label={r.count} width={(r.kg / max) * 300}
            colors={['var(--c-bar1)', 'var(--c-bar1)']}
            value={<>{num(r.kg)}<span className="muted"> · {Math.round(r.share)}%</span></>}
            tip={
              <>
                <TipTitle>Count {r.count}</TipTitle>
                <TipRow label="Output" value={`${num(r.kg)} kg`} color="var(--c-bar1)" />
                <TipRow label="Share of month" value={`${r.share.toFixed(1)}%`} />
                <TipRow label="Per operating day" value={`${num(r.kg / data.days)} kg`} />
              </>
            } />
        ))}
      </div>
    </Blueprint>
  )
}

function Stock({ data }: { data: MonthSummary }) {
  const s = data.stock
  const rows = s.items.slice(0, 7)
  const max = rows.length ? rows[0].qty : 1
  return (
    <Blueprint className="sheet">
      <PanelHead title="Yarn in stock" />
      <Note>
        {s.as_of
          ? `${num(s.on_hand)} kg on hand as of ${longDate(s.as_of)} — top items by weight, share of total on the right.`
          : 'No yarn stock recorded for this month.'}
      </Note>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {rows.map((r) => (
          <BarRow key={r.item} label={r.item} width={(r.qty / max) * 300}
            colors={['var(--c-bar5)', 'var(--c-bar5)']}
            value={<>{num(r.qty)}<span className="muted"> · {Math.round(r.share)}%</span></>}
            tip={
              <>
                <TipTitle>{r.item}</TipTitle>
                <TipRow label="On hand" value={`${num(r.qty)} kg`} color="var(--c-bar5)" />
                <TipRow label="Share of stock" value={`${r.share.toFixed(1)}%`} />
              </>
            } />
        ))}
      </div>
      <div style={{ marginTop: 18 }}>
        <TrendLine values={s.daily_totals} labelY={(v) => compactKg(v, 0)}
          yTitle="Total kg" xLabels={dayColumns(data, false)}
          tooltip={(i) => (
            <>
              <TipTitle>{dayOfMonth(data.dates[i])} {monthName(data.month, false).slice(0, 3)} · {data.series.dow[i]}</TipTitle>
              <TipRow label="Yarn in stock" value={`${num(s.daily_totals[i])} kg`} color="var(--c-ink)" />
              {i > 0 ? (
                <TipRow label="Change on previous"
                  value={`${s.daily_totals[i] - s.daily_totals[i - 1] >= 0 ? '+' : '−'}${num(Math.abs(s.daily_totals[i] - s.daily_totals[i - 1]))} kg`} />
              ) : null}
            </>
          )} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 8 }}>
        Total stock across the month
        {s.delta ? ` · ${s.delta > 0 ? 'up' : 'down'} ${num(Math.abs(s.delta))} kg from the first day recorded` : ''}
      </div>
    </Blueprint>
  )
}

function Power({ data }: { data: MonthSummary }) {
  const t = data.totals
  const solarShare = Math.round((t.solar / Math.max(1, t.eb + t.solar)) * 100)
  return (
    <Blueprint className="sheet">
      <PanelHead title="Power" />
      <Note>
        Daily units drawn — EB below, solar above — against units per kg of yarn
        (line, right axis).
      </Note>
      <Stacked
        height={240}
        lower={data.series.eb}
        upper={data.series.solar}
        colors={['var(--c-bar5)', 'var(--c-bar6)']}
        labelY={(v) => compactKg(v, 0)}
        yTitle="Units"
        line={data.series.ukg}
        lineLabelY={(v) => v.toFixed(1)}
        lineTitle="Units / kg"
        xLabels={dayColumns(data, true)}
        tooltip={(i) => (
          <>
            <TipTitle>{dayOfMonth(data.dates[i])} {monthName(data.month, false).slice(0, 3)} · {data.series.dow[i]}</TipTitle>
            <TipRow label="EB" value={num(data.series.eb[i])} color="var(--c-bar5)" />
            <TipRow label="Solar" value={num(data.series.solar[i])} color="var(--c-bar6)" />
            <TipRow label="Total" value={num(data.series.eb[i] + data.series.solar[i])} />
            <TipRow label="Units / kg"
              value={data.series.ukg[i] != null ? data.series.ukg[i]!.toFixed(2) : '–'} />
          </>
        )}
      />
      <Legend items={[
        { color: 'var(--c-bar5)', label: 'EB' },
        { color: 'var(--c-bar6)', label: `Solar · ${solarShare}% of draw` },
        { color: 'var(--color-accent-900)', line: true,
          label: `Average ${t.ukg_avg?.toFixed(2) ?? '–'} units per kg` },
      ]} />
    </Blueprint>
  )
}

function EfficiencyHeatmap({ data, sel, openDay, bench }: {
  data: MonthSummary; sel: number; openDay: (date: string) => void; bench: number
}) {
  const dark = useIsDark()
  const t = data.totals
  return (
    <Blueprint className="sheet">
      <PanelHead title="Utilization × efficiency heatmap" />
      <Note>
        Utilization is machine time actually run — machines against the
        {' '}{data.installed.in_view} installed, less the stoppage time booked that
        day — averaging {t.utilization.toFixed(1)}%. Efficiency is output against
        the workbook's standard from the machine settings, while running. The cell
        shows their product, the real share of
        installed capacity earned: month average {t.util_x_eff.toFixed(1)}%
        {' '}against a {bench}% benchmark. Blue is the month's strongest, red the
        weakest. Select a day to open it in the Daily report tab.
      </Note>
      <Heatmap
        dates={data.dates}
        efficiency={data.series.util_x_eff}
        selected={sel}
        dark={dark}
        onPick={(i) => openDay(data.dates[i])}
      />
    </Blueprint>
  )
}

function DayTable({ data, sel, onSelect, openDay, bench }: {
  data: MonthSummary; sel: number; onSelect: (i: number) => void
  openDay: (date: string) => void; bench: number
}) {
  const short = monthName(data.month, false).slice(0, 3)
  return (
    <Collapsible
      title="Day by day"
      defaultOpen={false}
      hint={`${data.table.length} days · select a row to open that day in the Daily report tab`}
    >
      <div className="scroll-x" style={{ marginTop: 12 }}>
        <table className="table" style={{ minWidth: 1020 }}>
          <thead>
            <tr>
              <th>Date</th><th style={R}>Actual kg</th><th style={R}>SITRA target</th>
              <th style={R}>Eff. SITRA</th><th style={R}>Eff. settings</th>
              <th style={{ width: 165 }}>Util × eff</th><th style={R}>Waste kg</th>
              <th style={R}>Waste %</th><th style={R}>Units/kg</th><th>Largest stoppage</th>
            </tr>
          </thead>
          <tbody>
            {data.table.map((r, i) => (
              <tr key={r.date} className={`clickable ${i === sel ? 'selected' : ''}`}
                onClick={() => { onSelect(i); openDay(r.date) }}>
                <td style={{ fontWeight: 600 }}>{dayOfMonth(r.date)} {short} · {r.dow}</td>
                <td className="num" style={R}>{num(r.actual)}</td>
                <td className="num" style={R}>{num(r.target)}</td>
                <td className="num" style={R}>{pct(r.efficiency)}</td>
                <td className="num" style={R}>{bothPct(r.efficiency_settings)}%</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg viewBox="0 0 70 8" style={{ width: 70, height: 8, display: 'block', flex: 'none' }}>
                      <rect x="0" y="1" width="70" height="6" fill="var(--c-track)" />
                      <rect x="0" y="1" width={Math.max(2, Math.min(70, (r.util_x_eff / 120) * 70))}
                        height="6" fill={effFill(r.util_x_eff, bench)} />
                    </svg>
                    <div>
                      <span className={`tag num ${tagFor(r.util_x_eff, bench)}`}>
                        {pct(r.util_x_eff)}
                      </span>
                      <div className="num muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {r.utilization.toFixed(1)}% × {bothPct(r.efficiency_settings)}%
                      </div>
                    </div>
                  </div>
                </td>
                <td className="num" style={R}>{num(r.waste)}</td>
                <td className="num" style={R}>{r.waste_pct.toFixed(2)}%</td>
                <td className="num" style={R}>{r.ukg?.toFixed(2) ?? '–'}</td>
                <td className="muted">
                  {r.top_reason}{r.top_reason_pct ? ` · ${pct(r.top_reason_pct)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Collapsible>
  )
}

function Footer({ data }: { data: MonthSummary }) {
  return (
    <footer className="pretty" style={{
      fontSize: 12, color: 'var(--color-neutral-600)',
      borderTop: '1px solid var(--color-divider)', paddingTop: 14,
    }}>
      {monthName(data.month, true)} · {data.days} days · {num(data.totals.actual)} kg produced
      against {num(data.totals.target)} kg at SITRA and {num(data.totals.target_settings)} kg
      at the workbook's machine settings. SITRA efficiency is output over SITRA gps × the
      spindles on each frame; settings efficiency is output over the workbook's Standard kg
      / Shift. Utilization × efficiency uses the settings figure. Waste is a share of actual output. Stoppage percentages are of available machine time
      and are summed across the month, so they exceed 100% over a period.
    </footer>
  )
}

// ==========================================================================
// Yearly
// ==========================================================================
function YearTab({ unit, openMonth }: { unit: Unit | null; openMonth: (m: string) => void }) {
  const { data, error, loading, reload } = useAsync(() => production.year(unit), [unit])

  if (error) return <ErrorBox error={error} onRetry={reload} />
  if (loading && !data) return <Loading what="the year" />
  if (!data || data.empty) {
    return <Empty title="Nothing on file yet">Load a workbook on the Data files tab.</Empty>
  }

  const rows = data.months
  const t = data.totals

  return (
    <>
      <Subline>
        {rows.length} month{rows.length === 1 ? '' : 's'} on file · {t.days} operating days
        {unit ? ` · ${unitLabel(unit)} only · power and overhead allocated by output share` : ''}
      </Subline>

      <KpiRow items={[
        { label: 'Output on file', value: num(t.actual / 1000, 1), unit: 't',
          note: `across ${rows.length} month${rows.length === 1 ? '' : 's'} and ${t.days} days` },
        { label: 'Util × efficiency', value: t.util_x_eff.toFixed(1), unit: '%',
          tone: t.util_x_eff >= data.benchmark ? 'good' : 'bad',
          note: 'weighted across every day loaded' },
        { label: 'Efficiency · SITRA', value: t.efficiency.toFixed(1), unit: '%',
          note: 'against SITRA gps × spindles' },
        { label: 'Efficiency · settings', value: bothPct(t.efficiency_settings), unit: '%',
          note: "against the workbook's machine settings" },
        { label: 'Shortfall', value: num(t.shortfall / 1000, 1), unit: 't',
          note: `against ${tonnes(t.target)} at SITRA` },
        { label: 'Waste', value: t.waste_pct.toFixed(2), unit: '%',
          note: `${num(t.waste)} kg over the period` },
        { label: 'Strongest month', value: data.best_month ? monthName(data.best_month.month) : '–',
          note: data.best_month
            ? `${pct(data.best_month.efficiency)} efficiency · ${num(data.best_month.actual)} kg` : '' },
      ]} />

      <Blueprint className="sheet">
        <PanelHead title="Month on month"
          hint="Outline is target, solid is actual, the line is utilization × efficiency on the right axis." />
        <div style={{ marginTop: 12 }}>
          <TargetActual
            height={260}
            target={rows.map((r) => r.target)}
            actual={rows.map((r) => r.actual)}
            efficiency={rows.map((r) => r.util_x_eff)}
            benchmark={data.benchmark}
            labelY={(v) => num(v / 1000, 0) + 't'}
            xLabels={rows.map((r) => (
              <div key={r.month} style={{ textAlign: 'center', lineHeight: 1.35 }}>
                <div className="num" style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-accent-800)' }}>
                  {tonnes(r.actual)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>{monthName(r.month)}</div>
                <div className="num" style={{ fontSize: 10, color: 'var(--color-neutral-600)' }}>
                  {pct(r.util_x_eff)}
                </div>
              </div>
            ))}
          />
        </div>
      </Blueprint>

      <Blueprint className="sheet">
        <PanelHead title="Every month on file" hint="Select a month to open it." />
        <div className="scroll-x" style={{ marginTop: 12 }}>
          <table className="table" style={{ minWidth: 1080 }}>
            <thead>
              <tr>
                <th>Month</th><th style={R}>Days</th><th style={R}>Actual kg</th>
                <th style={R}>SITRA target</th><th style={R}>Eff. SITRA</th>
                <th style={R}>Eff. settings</th><th style={R}>Util × eff</th><th style={R}>Waste %</th>
                <th style={R}>Avg kg/day</th><th style={R}>Units/kg</th><th>Largest stoppage</th>
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((r) => (
                <tr key={r.month} className="clickable" onClick={() => openMonth(r.month)}>
                  <td style={{ fontWeight: 600 }}>{monthName(r.month, true)}</td>
                  <td className="num" style={R}>{r.days}</td>
                  <td className="num" style={R}>{num(r.actual)}</td>
                  <td className="num" style={R}>{num(r.target)}</td>
                  <td className="num" style={R}>{pct(r.efficiency)}</td>
                  <td className="num" style={R}>{bothPct(r.efficiency_settings)}%</td>
                  <td className="num" style={R}>
                    {pct(r.util_x_eff)}
                    <div className="num muted" style={{ fontSize: 11 }}>
                      {r.utilization.toFixed(1)}% × {bothPct(r.efficiency_settings)}%
                    </div>
                  </td>
                  <td className="num" style={R}>{r.waste_pct.toFixed(2)}%</td>
                  <td className="num" style={R}>{num(r.avg_per_day)}</td>
                  <td className="num" style={R}>{r.ukg?.toFixed(2) ?? '–'}</td>
                  <td className="muted">{r.top_reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Blueprint>
    </>
  )
}

// ==========================================================================
// Data files
// ==========================================================================
function DataTab() {
  // A viewer sees the days on file and nothing that changes them.
  const canUpload = useCan('upload')
  // An editor hands files in on the Data loads page instead.
  const loadElsewhere = useCan('load') && !canUpload
  const { data, error, loading, reload } = useAsync(() => production.inventory(), [])
  const [status, setStatus] = useState('')
  const [warnings, setWarnings] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const upload = useCallback(async (files: FileList | null) => {
    if (!files?.length) return
    setBusy(true)
    setStatus(`Reading ${files.length} workbook${files.length > 1 ? 's' : ''}…`)
    try {
      const res = await production.upload([...files])
      const days = res.loaded.reduce((a, l) => a + l.days, 0)
      const parts = [`${days} day${days === 1 ? '' : 's'} loaded from ${res.loaded.length} workbook${res.loaded.length === 1 ? '' : 's'}.`]
      if (res.failed.length) {
        parts.push(res.failed.map((f) => `${f.filename}: ${f.error}`).join(' '))
      }
      setStatus(parts.join(' '))
      setWarnings(res.loaded.flatMap((l) => l.warnings))
      reload()
    } catch (e: any) {
      setStatus(e.message ?? String(e))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [reload])

  const removeDay = useCallback(async (date: string) => {
    if (!window.confirm(`Remove every row for ${date}? This cannot be undone from here.`)) return
    try {
      const res = await production.deleteDay(date)
      setStatus(`${date} removed — ${res.rows_removed} rows.`)
      reload()
    } catch (e: any) {
      setStatus(e.message ?? String(e))
    }
  }, [reload])

  return (
    <>
      <Subline>{canUpload ? 'Manage the days the dashboards are built from' : 'The days the dashboards are built from'}</Subline>

      {canUpload ? <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
        <PanelHead title="Add the daily tracker workbook" />
        <Note>
          Drop in the Single Day Machine Tracker .xlsx as the floor keeps it — one sheet per
          day, named like 01-09-2026. Every day the workbook contains replaces what was on
          file for that day; days it does not mention are left alone. Anything that looked
          wrong in a sheet is listed below after the load, and kept in the load log.
        </Note>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <label className={`btn btn-primary ${busy ? 'disabled' : ''}`}
            style={{ cursor: busy ? 'not-allowed' : 'pointer', position: 'relative', overflow: 'hidden' }}>
            {busy ? 'Reading…' : 'Choose workbook'}
            <input ref={fileRef} type="file" accept=".xlsx" multiple disabled={busy}
              onChange={(e) => upload(e.target.files)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
          </label>
          <a className="btn btn-secondary" href={production.exportUrl()} download="production-export.json">
            Export every table as JSON
          </a>
        </div>
        {status ? (
          <div style={{ fontSize: 13, color: 'var(--color-accent-700)', marginTop: 14 }}>{status}</div>
        ) : null}
        {warnings.length ? (
          <details style={{ fontSize: 13, marginTop: 10 }}>
            <summary style={{ cursor: 'pointer' }}>
              {warnings.length} thing{warnings.length === 1 ? '' : 's'} worth a look in the sheets
            </summary>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--color-neutral-600)' }}>
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </details>
        ) : null}
      </Blueprint> : null}
      {loadElsewhere ? <Blueprint className="sheet" style={{ padding: '18px 24px' }}><LoadElsewhere /></Blueprint> : null}

      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {loading && !data ? <Loading what="the file list" /> : null}

      {data ? (
        <Blueprint className="sheet">
          <PanelHead title="Days on file"
            hint={`${data.days.length} days · ${data.months.length} month${data.months.length === 1 ? '' : 's'}`} />
          <div className="scroll-x" style={{ marginTop: 12 }}>
            <table className="table" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Date</th><th>Loaded from</th><th style={R}>Shift rows</th>
                  <th style={R}>Ring frame kg</th><th style={R}>Rows with a stoppage</th>
                  <th style={R}>Meters</th>{canUpload ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.date}>
                    <td style={{ fontWeight: 600 }}>{d.date}</td>
                    <td className="muted">{d.sheet ?? '–'}</td>
                    <td className="num" style={R}>{d.items}</td>
                    <td className="num" style={R}>{num(d.actual)}</td>
                    <td className="num" style={R}>{d.stoppages}</td>
                    <td className="num" style={R}>{d.power ? 'Yes' : '–'}</td>
                    {canUpload ? (
                      <td style={R}>
                        <button className="btn btn-ghost" style={{ fontSize: 12 }}
                          onClick={() => removeDay(d.date)}>Remove</button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Blueprint>
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------- helpers
const R: React.CSSProperties = { textAlign: 'right' }

/** A percentage that may be missing — August has no settings on file. */
const bothPct = (v: number | null | undefined) => (v == null ? '–' : v.toFixed(1))

function tagFor(eff: number, bench: number): string {
  if (eff >= bench) return 'tag-accent'
  if (eff >= bench - 8) return 'tag-outline'
  return 'tag-neutral'
}
